import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';

/**
 * Whether the signed-in user may operate on a component owned by `ownerRef`
 * (a Group entity ref like `group:default/platform-group`).
 *
 * Mirrors the backend's `assertCanOperate`, which allows a user who is a direct
 * member of the owner group OR of any descendant group. Backstage's
 * `ownershipEntityRefs` already contains the user's groups and their ancestors,
 * so "owner ∈ ownershipEntityRefs" is exactly "owner is an ancestor-or-self of
 * one of the user's groups" — the same set the backend permits.
 *
 * Returns false until identity resolves (and for users who own nothing), so a
 * non-member never sees an enabled action. The backend still enforces the real
 * check; this only keeps other teams' buttons un-pressable in the UI.
 */
export function useOwnsEntity(ownerRef: string | undefined): boolean {
  const identityApi = useApi(identityApiRef);
  const { value } = useAsync(
    () => identityApi.getBackstageIdentity(),
    [identityApi],
  );
  if (!ownerRef || !value) return false;
  const target = ownerRef.toLowerCase();
  return (value.ownershipEntityRefs ?? []).some(
    ref => ref.toLowerCase() === target,
  );
}
